/**
 * La escala de ESCALONES del percentil de un DEP.
 *
 * LA CONSUMEN LAS DOS PANTALLAS: `BarraDep` en la portada (`BloqueToro.jsx`) y
 * `FilaDep` en la ficha (`AnimalCatalogoPage.jsx`). Y tiene que seguir siendo
 * así: el comprador ve el color en la tarjeta de la portada y lo vuelve a ver
 * en la ficha del animal, a dos clicks, y ahí tiene que significar lo MISMO.
 * Antes de este archivo cada pantalla tenía su propio umbral binario
 * (`PERCENTIL_DESTACADO = 25`, definido por separado en las dos) — funcionaba
 * de casualidad porque las dos copias decían lo mismo, y se rompió en el
 * instante en que una de las dos evolucionó. Lo que diferencia a las pantallas
 * es la CANTIDAD de DEPs, no cómo se leen.
 *
 * De dónde sale
 * -------------
 * Feedback de un cabañero sobre la ficha del catálogo (audios del 2026-09-10).
 * Validó el enfoque de barra —"muy didáctico", "un pantallazo rápido", contra
 * la tabla clásica de la asociación que es "tres datos por cuadradito" y se
 * vuelve tediosa cuando hay que barrer un catálogo entero— y pidió UNA cosa:
 *
 *   "al fondo de la línea, donde dice el top 1, top 2, top 3... yo ahí haría
 *    con un color, con una letra más importante, EN VEZ DE DEJARLO EN UNA
 *    ESCALA DE GRISES, QUE POR AHÍ SE PIERDE... del uno al cinco un color,
 *    del uno al diez un color, del veinte al treinta otro color... por lo
 *    menos los primeros 15"
 *
 * Tenía razón literalmente: el `top N%` se dibujaba con el gris secundario de
 * antd a 11px. El dato más accionable de la fila era el más apagado.
 *
 * Por qué la paleta es FIJA y no sale de `config.color_primary`
 * ------------------------------------------------------------
 * Porque el percentil es una escala OBJETIVA de la raza, no un elemento de
 * marca. Si cada cabaña pintara su "top 5%" del color que quiere, el comprador
 * tendría que aprender el código de nuevo en cada catálogo que abre. Unificarlo
 * no es una concesión al branding: es una ventaja del producto. La card de DEPs
 * funciona como un INSTRUMENTO DE MEDICIÓN, y un instrumento se lee igual en
 * todas partes. La marca de la cabaña vive en el resto de la página —cabecera,
 * hero, botones, títulos— y ahí no se toca.
 *
 * Por qué verde -> ámbar y NUNCA rojo/verde
 * -----------------------------------------
 * El público de un catálogo de genética es abrumadoramente masculino, y cerca
 * del 8% de los varones tiene alguna deficiencia en la visión del color: el par
 * rojo/verde es justo el que peor funciona. Además los tonos van ordenados por
 * LUMINOSIDAD creciente, así que los escalones se distinguen incluso sin
 * percibir el tono — en escala de grises siguen siendo tres pasos distintos.
 *
 * Y el color nunca es el único portador: el número (`TOP 5%`) queda SIEMPRE
 * escrito al lado. El color refuerza, no reemplaza.
 *
 * Por qué TRES escalones y no los cuatro del boceto
 * -------------------------------------------------
 * El cabañero tiró "1 al 5, 1 al 10, 10 al 20, 20 al 30" y aclaró "habría que
 * verlo" — era un boceto, y se le solapan las bandas. Tres pasos se aprenden de
 * un vistazo; en el cuarto ya hace falta una leyenda, y una leyenda es lo que
 * estábamos tratando de evitar. Los cortes respetan su pedido explícito de
 * cubrir "por lo menos los primeros 15".
 */

// De mejor a peor. El primero que matchea gana, así que el orden IMPORTA.
const ESCALONES = [
  {
    clave: 'elite',
    hasta: 5,
    etiqueta: 'Élite',
    color: '#14532d',
    // Sólo el escalón de arriba lleva fondo. En una ficha con diez DEPs, que
    // los tres primeros escalones tuvieran chip sería diez chips: el realce
    // deja de realzar cuando lo tiene todo.
    fondo: 'rgba(20, 83, 45, 0.10)',
  },
  // El corte en 20 y no en 15: el cabañero dijo "POR LO MENOS los primeros 15",
  // y "por lo menos" es un PISO, no un techo — leerlo como techo fue el primer
  // error de calibración. Y el número aguanta solo: estar en el top 20% de una
  // raza es uno de cada cinco animales, eso no es "decente", es bueno.
  { clave: 'muy_alto', hasta: 20, etiqueta: 'Muy alto', color: '#15803d', fondo: null },
  { clave: 'alto', hasta: 30, etiqueta: 'Alto', color: '#a16207', fondo: null },
];

/**
 * El escalón que le toca a un percentil, o `null` si no hay ninguno.
 *
 * OJO CON EL SENTIDO: `percentil` es el rank oficial de la asociación y **1 es
 * el MEJOR, 100 el peor** (verificado contra datos reales: el mejor DEP de
 * destete, 12.84, tiene rank 3; el peor, −1.27, tiene 97). Por eso se compara
 * con `<=` y los cortes van de chico a grande.
 *
 * @param {number|null} percentil
 * @returns {{clave: string, etiqueta: string, color: string, fondo: string|null}|null}
 *   `null` cuando no hay percentil, o cuando está por debajo del último
 *   escalón: pasado el 30% el número ya no dice "mirame", y forzar un color
 *   ahí convertiría la escala en decoración.
 */
export const escalonDe = (percentil) => {
  // `!= null` y no truthy: un percentil de 0 no existe en la escala (arranca en
  // 1), pero si algún día llegara, un `if (!percentil)` lo tiraría como vacío.
  if (percentil == null) return null;
  return ESCALONES.find((e) => percentil <= e.hasta) || null;
};
